import React, { useState, useEffect } from 'react';
import {
	Button,
	Col,
	Input,
	Row,
	Label,
	ModalBody,
	Form,
	TabPane,
	TabContent,
	Spinner,
	FormFeedback,
	FormGroup,
} from 'reactstrap';

// Formik
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

//redux
import { addNewCompany, updateCompany } from '../../../../slices/company/thunk';
import { setRequireCloseModal } from '../../../../slices/company/reducer';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import RolesSelect from '../../../../Components/General/Selects/RolesSelect';
import GeneralSelect from '../../../../Components/General/Selects/GeneralSelect';
import {
	getCommuneList,
	getRegionList,
	getCompanyList,
	getCompanyTypeList,
	getHoldingList,
	getProductiveAreaList,
	getSanitaryVicinityList,
} from '../../../../helpers/establishment/backend_helper';
import OSelect from '../../../../Components/OElements/OSelect';
import OInput from '../../../../Components/OElements/OInput';

const CompanyForm = ({
	dataCompany,
	isEdit,
	handleToggleModal,
	isOpen,
	activeTab,
	toggleTab,
}) => {
	const dispatch = useDispatch();

	const [selectedVisibility, setselectedVisibility] = useState(null);

	const [selectedHolding, setSelectedHolding] = useState(null);
	const handleHoldingSelect = optionSelected => {
		setSelectedHolding(optionSelected);
		validation.setFieldValue('holding_company_id', optionSelected?._id ?? '');
	};

	const selectorCompany = createSelector(
		state => state.Companies,
		company => ({
			isAdding: company.isAdding,
			isAdded: company.isAdded,
			isUpdating: company.isUpdating,
			isUpdated: company.isUpdated,
			isLoadingList: company.isLoadingList,
			requireCloseModal: company.requireCloseModal,
		})
	);
	const {
		isAdding,
		isAdded,
		isUpdating,
		isUpdated,
		isLoadingList,
		requireCloseModal,
	} = useSelector(selectorCompany);

	// validation
	const validation = useFormik({
		enableReinitialize: true, // Permite reestablecer valores iniciales si cambian
		initialValues: {
			name: dataCompany?.name ?? '',
			initials: dataCompany?.initials ?? '',
			rut: dataCompany?.rut ?? '',
			email: dataCompany?.email ?? '',
			phono: dataCompany?.phono ?? '',
			disabled: dataCompany?.disabled ?? false,
			holding_company_id: dataCompany?.holding_company_id ?? '',
			address: dataCompany?.address ?? '',
			business_activity: dataCompany?.business_activity ?? '',
			business_activity_code: dataCompany?.business_activity_code ?? '',
			commune_id: dataCompany?.commune_id ?? '',
			region_id: dataCompany?.region_id ?? '',
			dte_emisor: dataCompany?.dte_emisor ?? false,
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Campo requerido'),
			initials: Yup.string().required('Campo requerido'),
			rut: Yup.string().required('Campo requerido'),
			email: Yup.string()
				.email('El correo electrónico ingresado no es válido')
				.required('Campo requerido'),
			phono: Yup.number()
				.typeError('Debe ser un número válido')
				.required('Campo requerido'),
			holding_company_id: Yup.string().required('Campo requerido'),
			address: Yup.string().required('Campo requerido'),
			business_activity: Yup.string(),
			business_activity_code: Yup.string(),
			commune_id: Yup.string(),
			region_id: Yup.string(),
			dte_emisor: Yup.boolean(),
		}),
		onSubmit: values => {
			// Construye el objeto base de la compañía
			const company = {
				...values,
			};

			if (isEdit) {
				// Actualiza la compañía existente
				dispatch(updateCompany({ ...company, _id: dataCompany._id }));
			} else {
				// Crea una nueva compañía
				dispatch(addNewCompany(company));
			}
		},
	});

	useEffect(() => {
		if (isOpen) {
			if (requireCloseModal) {
				dispatch(setRequireCloseModal(false));
				handleToggleModal();
			}
		}
	}, [requireCloseModal]);

	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length != 0) {
					toast.error(
						'Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.',
						{ autoClose: 4000 }
					);
				}
				validation.handleSubmit();
				return false;
			}}
		>
			<ModalBody className='modal-body'>
				<TabContent activeTab={activeTab}>
					<TabPane tabId='1'>
						<Row className='g-3'>
							<Col lg={6}>
								<OInput
									name='name'
									label='Nombre de la Empresa'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='initials'
									label='Sigla de la Empresa'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='rut'
									label='RUT'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={6}>
								<OSelect
									key={`holding_company_id` || ''}
									label='Holding'
									id='holding_company_id'
									name='holding_company_id'
									isRequired={true}
									value={dataCompany?.holding_company_id || ''}
									onSelect={handleHoldingSelect}
									requestApi={() => getHoldingList()}
									validation={validation}
								/>
							</Col>
							<Col lg={6}>
								<OInput
									name='address'
									label='Dirección de la Empresa'
									placeholder='Ingrese una dirección'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
						</Row>
					</TabPane>
					<TabPane tabId='2'>
						<Row className='g-3'>
							<Col lg={6}>
								<OInput
									name='email'
									label='Correo Electrónico'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={6}>
								<OInput
									name='phono'
									label='Celular de Contacto'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
						</Row>
					</TabPane>
					<TabPane tabId='3'>
						<Row className='g-3'>
							<Col lg={9}>
								<OInput
									name='business_activity'
									label='Actividad Económica'
									type='text'
									validation={validation}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='business_activity_code'
									label='Código de Actividad Económica'
									type='text'
									validation={validation}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={4}>
								<OSelect
									label='Región'
									id='region_id'
									name='region_id'
									isRequired={true}
									value={validation.values.region_id || ''}
									requestApi={() => getRegionList({})}
									disabled={false}
									validation={validation}
								/>
							</Col>
							<Col lg={4}>
								<OSelect
									key={`commune_id`}
									label='Comuna'
									id='commune_id'
									name='commune_id'
									isRequired={true}
									value={validation.values.commune_id || ''}
									requestApi={() =>
										getCommuneList({
											region_id: validation.values.region_id,
										})
									}
									disabled={!validation.values.region_id} // Deshabilitar si no hay región seleccionada
									validation={validation}
								/>
							</Col>
							<Col lg={4}>
								<div className='form-check form-switch'>
									<Input
										className='form-check-input'
										type='checkbox'
										role='switch'
										id={`SwitchCheck_dte_emisor`}
										checked={!!validation.values.dte_emisor}
										onChange={e =>
											validation.setFieldValue(
												'dte_emisor',
												e.target.checked
											)
										}
									/>
									<Label
										className='form-check-label ms-2'
										htmlFor={`SwitchCheck_dte_emisor`}
									>
										{validation.values.dte_emisor
											? 'Es Emisor DTE'
											: 'No es Emisor DTE'}
									</Label>
								</div>
							</Col>
						</Row>
					</TabPane>
				</TabContent>
			</ModalBody>
			<div className='modal-footer'>
				<div className='hstack gap-2 justify-content-end'>
					<Button
						type='button'
						onClick={() => {
							handleToggleModal();
						}}
						className='btn-danger btn-label'
					>
						<i className='icon-x label-icon'></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						id='add-btn'
						disabled={isAdding || isUpdating}
					>
						{isAdding || isUpdating ? (
							<i className='label-icon'>
								<Spinner size='sm' />
							</i>
						) : (
							<i className='ri-save-3-line label-icon'></i>
						)}
						{isEdit ? 'Editar' : 'Crear'} Empresa
					</button>
				</div>
			</div>
		</Form>
	);
};

export default CompanyForm;
